import React from 'react';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Chip from '@mui/material/Chip';
import TablePagination from '@mui/material/TablePagination';
import { Badge } from '@mui/material';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';

import { TablePaginationActionsV5 } from 'retired/TablePaginationActions';
import { STATUSES } from 'containers/PayrollFlatFilePage/PayrollRuns';
import { RetryModal } from 'containers/PayrollGenericPage/RetryModal';
import {
  useRecreateDonationForTheNextPayrollRunFlatFileMutation,
  usePayrollRunsFlatFileListWithPaginationQuery,
  PayrollRunFlatFile,
  PayrollRunFlatFileStateType,
} from 'generated/graphql';
import {
  green,
  red,
  primaryBlue,
  purpleish,
  lightYellow,
  black,
  lightGreen,
} from 'utils/color-styles';
import { currencyFormat } from 'utils/currencyFormat';

import { ExportLink } from './PayrollRun';

type Props = {
  status: STATUSES.UPCOMING | STATUSES.RECENT;
  payrollPayGroup: { id: string; payGroupDisplayName: string };
};

interface Pagination {
  take: number;
  skip: number;
  totalItems: number;
  totalPages: number;
}

export const PayrollRunsTableUpcoming = ({ status, payrollPayGroup }: Props) => {
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [retryModalForDonation, setRetryModalForDonation] = React.useState<string | undefined>();
  const [updateUnresolvedDonation, updateState] =
    useRecreateDonationForTheNextPayrollRunFlatFileMutation({
      onCompleted: () => setRetryModalForDonation(undefined),
    });

  const payrollRunsQuery = usePayrollRunsFlatFileListWithPaginationQuery({
    variables: {
      payrollPayGroupId: payrollPayGroup.id,
      status,
      take: rowsPerPage,
      skip: rowsPerPage * page,
    },
    skip: !payrollPayGroup.id,
  });

  const displayedRuns = payrollRunsQuery?.data?.payrollRunsFlatFileWithPagination?.items || [];
  const pagination: Pagination = payrollRunsQuery?.data?.payrollRunsFlatFileWithPagination
    .metadata || {
    skip: 0,
    take: rowsPerPage,
    totalItems: 0,
    totalPages: 0,
  };

  return (
    <>
      <RetryModal
        isOpen={!!retryModalForDonation}
        onClose={() => setRetryModalForDonation(undefined)}
        onConfirm={() =>
          retryModalForDonation &&
          updateUnresolvedDonation({
            variables: { donationId: retryModalForDonation, shouldCancel: false },
          })
        }
        onCancelDonation={() =>
          retryModalForDonation &&
          updateUnresolvedDonation({
            variables: { donationId: retryModalForDonation, shouldCancel: true },
          })
        }
        isLoading={updateState.loading}
        error={updateState.error}
      />
      <TableContainer component={Paper} sx={{ borderRadius: 4 }}>
        {payrollRunsQuery.loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell size="medium">Pay Group</TableCell>
              <TableCell size="medium">Starting date</TableCell>
              <TableCell size="medium">End date</TableCell>
              <TableCell size="medium">Expected Recon File Date</TableCell>
              <TableCell size="medium" style={{ textAlign: 'center' }}>
                Last date for Deductions
                <span>
                  <br />
                  (cut-off date)
                </span>
              </TableCell>
              <TableCell size="medium">Status</TableCell>
              <TableCell size="medium">Download</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedRuns.map((payrollRun) => (
              <TableRow
                key={payrollRun.id}
                sx={{ '&:hover': { backgroundColor: `${purpleish} !important` } }}
              >
                <TableCell
                  size="medium"
                  style={{ lineHeight: '32px' }}
                  component={Link}
                  to={`/donations/payroll/run/${payrollRun.id}/${payrollPayGroup.id}`}
                >
                  {payrollPayGroup.payGroupDisplayName}
                  {status === STATUSES.RECENT &&
                    payrollRun.stats.donationCountPendingOrUnresolved > 0 && (
                      <Badge
                        variant="dot"
                        color="error"
                        style={{ marginLeft: 8 }}
                        title="The payroll run contains unresolved donations"
                      />
                    )}
                </TableCell>
                <TableCell
                  size="medium"
                  component={Link}
                  to={`/donations/payroll/run/${payrollRun.id}/${payrollPayGroup.id}`}
                >
                  <Typography style={{ lineHeight: '32px', cursor: 'pointer', color: primaryBlue }}>
                    {moment(payrollRun.startDateExternal as string).format('ll')}
                  </Typography>
                </TableCell>
                <TableCell
                  size="medium"
                  component={Link}
                  to={`/donations/payroll/run/${payrollRun.id}/${payrollPayGroup.id}`}
                >
                  <Typography style={{ lineHeight: '32px', cursor: 'pointer', color: primaryBlue }}>
                    {moment(payrollRun.endDateExternal as string).format('ll')}
                  </Typography>
                </TableCell>
                <TableCell
                  size="medium"
                  component={Link}
                  to={`/donations/payroll/run/${payrollRun.id}/${payrollPayGroup.id}`}
                >
                  <Typography style={{ lineHeight: '32px', cursor: 'pointer', color: primaryBlue }}>
                    {moment(payrollRun.payDate as string).format('ll')}
                  </Typography>
                </TableCell>
                <TableCell
                  size="medium"
                  component={Link}
                  to={`/donations/payroll/run/${payrollRun.id}/${payrollPayGroup.id}`}
                >
                  <Typography style={{ lineHeight: '32px', cursor: 'pointer', color: primaryBlue }}>
                    {moment(payrollRun.endDate as string).format('ll')}
                  </Typography>
                </TableCell>
                <TableCell size="medium">
                  <Chip
                    label={payrollRun.runState.toUpperCase()}
                    size="small"
                    style={{ backgroundColor: '#EEE0FF', color: '#9264CC' }}
                  />
                </TableCell>
                <TableCell size="medium" style={{ width: '1%' }}>
                  <ExportLink
                    payrollRunId={payrollRun.id}
                    filters={{ payrollPayGroupId: payrollPayGroup.id }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TablePagination
            count={pagination.totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_event, newPage): void => setPage(newPage)}
            onChangePage={(_event, newPage): void => setPage(newPage)}
            onRowsPerPageChange={(event): void => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
              native: true,
            }}
            ActionsComponent={TablePaginationActionsV5}
          />
        </Table>
      </TableContainer>
    </>
  );
};

export const PayrollRunsTableRecents = ({ status, payrollPayGroup }: Props) => {
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [retryModalForDonation, setRetryModalForDonation] = React.useState<string | undefined>();
  const [updateUnresolvedDonation, updateState] =
    useRecreateDonationForTheNextPayrollRunFlatFileMutation({
      onCompleted: () => setRetryModalForDonation(undefined),
    });

  const payrollRunsQuery = usePayrollRunsFlatFileListWithPaginationQuery({
    variables: {
      payrollPayGroupId: payrollPayGroup.id,
      status,
      take: rowsPerPage,
      skip: rowsPerPage * page,
    },
    skip: !payrollPayGroup.id,
  });

  const displayedRuns = payrollRunsQuery?.data?.payrollRunsFlatFileWithPagination?.items || [];
  const pagination: Pagination = payrollRunsQuery?.data?.payrollRunsFlatFileWithPagination
    .metadata || {
    skip: 0,
    take: rowsPerPage,
    totalItems: 0,
    totalPages: 0,
  };

  const getChangeComponent = (
    previousPayrollRun: PayrollRunFlatFile,
    currentPayrollRun: PayrollRunFlatFile
  ) => {
    if (
      previousPayrollRun.stats.totalUsd === currentPayrollRun.stats.totalUsd ||
      !previousPayrollRun.stats.totalUsd ||
      !currentPayrollRun.stats.totalUsd
    ) {
      return null;
    }

    const change = currentPayrollRun.stats.totalUsd - previousPayrollRun.stats.totalUsd;
    const changePercentage = Math.round((change / previousPayrollRun.stats.totalUsd) * 100);

    if (changePercentage > 0) {
      return (
        <span style={{ lineHeight: '32px', color: green }}>
          <ArrowDropUp /> {Math.abs(changePercentage)}%
        </span>
      );
    } else {
      return (
        <span style={{ lineHeight: '32px', color: red }}>
          <ArrowDropDown /> {Math.abs(changePercentage)}%
        </span>
      );
    }
  };

  return (
    <>
      <RetryModal
        isOpen={!!retryModalForDonation}
        onClose={() => setRetryModalForDonation(undefined)}
        onConfirm={() =>
          retryModalForDonation &&
          updateUnresolvedDonation({
            variables: { donationId: retryModalForDonation, shouldCancel: false },
          })
        }
        onCancelDonation={() =>
          retryModalForDonation &&
          updateUnresolvedDonation({
            variables: { donationId: retryModalForDonation, shouldCancel: true },
          })
        }
        isLoading={updateState.loading}
        error={updateState.error}
      />
      <TableContainer component={Paper} sx={{ borderRadius: 4 }}>
        {payrollRunsQuery.loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell size="medium">Pay Group</TableCell>
              <TableCell size="medium">Starting Date</TableCell>
              <TableCell size="medium">End Date</TableCell>
              <TableCell size="medium">Expected Recon File Date</TableCell>
              <TableCell size="medium">Completed Date</TableCell>
              <TableCell size="medium">Total Amount</TableCell>
              <TableCell size="medium">Status</TableCell>
              <TableCell size="medium">Download</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedRuns.map((payrollRun, idx) => (
              <TableRow
                key={payrollRun.id}
                sx={{ '&:hover': { backgroundColor: `${purpleish} !important` } }}
              >
                <TableCell
                  size="medium"
                  style={{ lineHeight: '32px' }}
                  component={Link}
                  to={`/donations/payroll/run/${payrollRun.id}/${payrollPayGroup.id}`}
                >
                  {payrollPayGroup.payGroupDisplayName}
                  {status === STATUSES.RECENT &&
                    payrollRun.stats.donationCountPendingOrUnresolved > 0 && (
                      <Badge
                        variant="dot"
                        color="error"
                        style={{ marginLeft: 8 }}
                        title="The payroll run contains unresolved donations"
                      />
                    )}
                </TableCell>
                <TableCell
                  size="medium"
                  component={Link}
                  to={`/donations/payroll/run/${payrollRun.id}/${payrollPayGroup.id}`}
                >
                  <Typography style={{ lineHeight: '32px', cursor: 'pointer', color: primaryBlue }}>
                    {moment(payrollRun.startDateExternal as string).format('ll')}
                  </Typography>
                </TableCell>
                <TableCell
                  size="medium"
                  component={Link}
                  to={`/donations/payroll/run/${payrollRun.id}/${payrollPayGroup.id}`}
                >
                  <Typography style={{ lineHeight: '32px', cursor: 'pointer', color: primaryBlue }}>
                    {moment(payrollRun.endDateExternal as string).format('ll')}
                  </Typography>
                </TableCell>
                <TableCell
                  size="medium"
                  component={Link}
                  to={`/donations/payroll/run/${payrollRun.id}/${payrollPayGroup.id}`}
                >
                  <Typography style={{ lineHeight: '32px', cursor: 'pointer', color: primaryBlue }}>
                    {moment(payrollRun.payDate as string).format('ll')}
                  </Typography>
                </TableCell>
                <TableCell
                  size="medium"
                  component={Link}
                  to={`/donations/payroll/run/${payrollRun.id}/${payrollPayGroup.id}`}
                >
                  <Typography style={{ lineHeight: '32px', cursor: 'pointer', color: primaryBlue }}>
                    {moment(payrollRun.modifiedAt as string).format('ll')}
                  </Typography>
                </TableCell>
                <TableCell
                  size="medium"
                  component={Link}
                  to={`/donations/payroll/run/${payrollRun.id}/${payrollPayGroup.id}`}
                >
                  <Typography style={{ lineHeight: '32px', cursor: 'pointer', color: primaryBlue }}>
                    {currencyFormat(payrollRun.stats.totalUsd, 'USD')}
                    <span style={{ marginLeft: 5 }}>
                      {idx < displayedRuns.length - 1 &&
                        displayedRuns[idx + 1] &&
                        getChangeComponent(displayedRuns[idx + 1], payrollRun)}
                    </span>
                  </Typography>
                </TableCell>
                <TableCell size="medium">
                  <Chip
                    label={payrollRun.runState.toUpperCase()}
                    size="small"
                    style={{
                      backgroundColor:
                        payrollRun.runState === PayrollRunFlatFileStateType.Processing ||
                        payrollRun.runState === PayrollRunFlatFileStateType.Pending
                          ? lightYellow
                          : lightGreen,
                      color: black,
                    }}
                  />
                </TableCell>
                <TableCell size="medium" style={{ width: '1%' }}>
                  <ExportLink
                    payrollRunId={payrollRun.id}
                    filters={{ payrollPayGroupId: payrollPayGroup.id }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TablePagination
            count={pagination.totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_event, newPage): void => setPage(newPage)}
            onChangePage={(_event, newPage): void => setPage(newPage)}
            onRowsPerPageChange={(event): void => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
              native: true,
            }}
            ActionsComponent={TablePaginationActionsV5}
          />
        </Table>
      </TableContainer>
    </>
  );
};
